import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Coordination } from './models';
import { baseApi } from '@centerpiece/components';
import { project } from './project.api';

export const coordination = baseApi
  .enhanceEndpoints({ addTagTypes: ['Coordinations', 'Coordination'] })
  .injectEndpoints({
    endpoints: builder => ({
      getCoordinations: builder.query<Coordination[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'coordination',
          method: 'GET',
          params: params ? params : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Coordination' as const,
                  id,
                })),
                'Coordinations',
              ]
            : ['Coordinations'],
      }),
      getCoordination: builder.query<
        Coordination,
        { id: number; params?: object }
      >({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `coordination/${id}`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) => [
          { type: 'Coordination', id: arg.id },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
        transformResponse(data: Coordination, meta, arg) {
          return {
            ...data,
            status_r: { ...(data?.crd_status_r || {}) },
            relationships: [...(data?.crd_relationships || [])],
          };
        },
      }),
      createCoordination: builder.mutation({
        query: request => ({
          url: 'coordination',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Coordinations'],
      }),
      patchCoordination: builder.mutation<Coordination, Coordination>({
        query: request => ({
          url: `coordination/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Coordination', id: arg.id },
        ],
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            coordination.util.updateQueryData(
              'getCoordination',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, {
                  id,
                  project: pid,
                  ...patch,
                  status_r: patch.crd_status_r,
                });
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(
              coordination.util.invalidateTags([
                { type: 'Coordination', id: id },
              ])
            );
          }
        },
      }),
      updateCoordination: builder.mutation<Coordination, Coordination>({
        query: request => ({
          url: `coordination/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Coordination', id: arg.id },
        ],
        async onQueryStarted(
          { id, project: pid, ...patch },
          { dispatch, queryFulfilled }
        ) {
          const patchResult = dispatch(
            coordination.util.updateQueryData(
              'getCoordination',
              { id, params: { nested: 'all' } },
              draft => {
                Object.assign(draft, {
                  id,
                  project: pid,
                  ...patch,
                  status_r: patch.crd_status_r,
                });
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(task.util.invalidateTags(['Tasks']))
             */
            dispatch(
              coordination.util.invalidateTags([
                { type: 'Coordination', id: id },
              ])
            );
          }
        },
      }),
      deleteCoordination: builder.mutation<Coordination, number>({
        query: id => ({
          url: `coordination/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Coordination' as const, arg }, 'Coordinations']
            : ['Coordinations'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchCoordinationMutation,
  useGetCoordinationsQuery,
  useGetCoordinationQuery,
  useCreateCoordinationMutation,
  useUpdateCoordinationMutation,
  useDeleteCoordinationMutation,
  useLazyGetCoordinationQuery,
  usePrefetch: useCoordinationsPrefetch,
} = coordination;
const selectCoordinationsRoot = coordination.endpoints.getCoordinations.select(
  {}
);
export const selectCoordinations = createSelector(
  selectCoordinationsRoot,
  coordination => coordination.data || []
);

export const subscribeCoordination =
  coordination.endpoints.getCoordination.useQueryState;
